<script setup>
import { ref, watch, nextTick } from 'vue'

const props = defineProps({
  modelValue: { type: String, default: '' },
  hasError: { type: Boolean, default: false },
  length: { type: Number, default: 6 }
})

const emit = defineEmits(['update:modelValue', 'complete'])

const inputRef = ref(null)

function onInput(event) {
  const raw = event.target.value.replace(/\D/g, '').slice(0, props.length)
  event.target.value = raw
  emit('update:modelValue', raw)
  if (raw.length === props.length) {
    emit('complete', raw)
  }
}

function onPaste(event) {
  event.preventDefault()
  const pasted = (event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, props.length)
  if (inputRef.value) inputRef.value.value = pasted
  emit('update:modelValue', pasted)
  if (pasted.length === props.length) {
    emit('complete', pasted)
  }
}

function onKeydown(event) {
  // Allow navigation, deletion, paste, select-all
  const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End']
  if (allowed.includes(event.key)) return
  if (event.metaKey || event.ctrlKey) return
  // Block non-digit keys
  if (!/^\d$/.test(event.key)) {
    event.preventDefault()
  }
}

// Sync inward if modelValue is cleared externally
watch(() => props.modelValue, (val) => {
  if (inputRef.value && inputRef.value.value !== val) {
    inputRef.value.value = val
  }
})

function focus() {
  inputRef.value?.focus()
}

function clear() {
  emit('update:modelValue', '')
  if (inputRef.value) inputRef.value.value = ''
  nextTick(() => inputRef.value?.focus())
}

defineExpose({ focus, clear })
</script>

<template>
  <div class="code-input-wrapper">
    <input
      ref="inputRef"
      type="text"
      inputmode="numeric"
      autocomplete="one-time-code"
      :maxlength="length"
      class="code-input"
      :class="{ 'is-danger': hasError }"
      :value="modelValue"
      placeholder="------"
      @input="onInput"
      @paste="onPaste"
      @keydown="onKeydown"
    />
  </div>
</template>

<style scoped lang="scss">
.code-input-wrapper {
  display: flex;
  justify-content: center;
}

.code-input {
  font-family: 'Mona Sans', ui-monospace, 'SF Mono', SFMono-Regular, monospace;
  font-size: 1.75rem;
  font-weight: 600;
  letter-spacing: 0.75em;
  text-align: center;
  width: 100%;
  max-width: 320px;
  padding: 12px 16px 12px 28px;
  border: 2px solid #dbdbdb;
  border-radius: 8px;
  outline: none;
  background: #fff;
  color: #363636;
  transition: border-color 0.15s, box-shadow 0.15s;
  caret-color: #00BFFF;

  &:focus {
    border-color: #00BFFF;
    box-shadow: 0 0 0 2px rgba(0, 191, 255, 0.2);
  }

  &.is-danger {
    border-color: #f14668;

    &:focus {
      box-shadow: 0 0 0 2px rgba(241, 70, 104, 0.2);
    }
  }

  &::placeholder {
    letter-spacing: 0.5em;
    color: #ccc;
    font-weight: 400;
  }

  // Remove spinner arrows
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}
</style>
